import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Crown, Copy, Loader2, RefreshCw, BarChart3, Wallet, TrendingUp, Layers, Activity } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { RoleBadge } from "@/components/Badges";
import { Sparkline } from "@/components/Sparkline";
import { getExchange } from "@/data/exchanges";
import { fmtUsd, fmtPct, pnlColor } from "@/lib/format";
import { cn } from "@/lib/utils";

const SummaryStat = ({ icon: Icon, label, value, valueClass, testid }) => (
  <div className="flex items-center gap-3 px-5 py-4" data-testid={testid}>
    <div className="w-9 h-9 rounded-sm bg-[rgba(212,175,55,0.08)] border border-[rgba(212,175,55,0.15)] flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.75} />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[#737373]">{label}</div>
      <div className={cn("font-mono text-lg font-semibold mt-0.5", valueClass || "text-[#F5F5F5]")}>{value}</div>
    </div>
  </div>
);

export default function Portfolio() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [syncing, setSyncing] = useState(false);

  const { data: summary } = useQuery({ queryKey: ["pf-summary"], queryFn: async () => (await api.get("/portfolio/summary")).data });
  const { data: accounts = [], isLoading } = useQuery({ queryKey: ["pf-accounts"], queryFn: async () => (await api.get("/portfolio/accounts")).data });

  const syncAll = async () => {
    setSyncing(true);
    try {
      await api.post("/portfolio/sync-all");
      qc.invalidateQueries({ queryKey: ["pf-accounts"] });
      qc.invalidateQueries({ queryKey: ["pf-summary"] });
      toast.success("All accounts synced");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  return (
    <div>
      <PageHeader
        title="Portfolio Tracker"
        subtitle="Performance across every connected master and connected account."
        pageKey="portfolio"
        testid="portfolio-header"
        actions={
          <button onClick={syncAll} disabled={syncing} data-testid="sync-all-btn"
            className="flex items-center gap-2 border border-[#333333] hover:border-[#D4AF37] hover:text-[#D4AF37] text-[#F5F5F5] text-sm rounded-sm px-4 py-2 transition-colors disabled:opacity-60">
            <RefreshCw className={cn("w-4 h-4", syncing && "animate-spin")} /> Sync All
          </button>
        }
      />

      <div className="rounded-md border border-[#262626] bg-[#141414] grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#262626] mb-6" data-testid="portfolio-summary-strip">
        <SummaryStat icon={Wallet} label="Total Balance" value={fmtUsd(summary?.total_balance)} testid="summary-balance" />
        <SummaryStat icon={TrendingUp} label="Avg Return" value={fmtPct(summary?.avg_return)} valueClass={pnlColor(summary?.avg_return)} testid="summary-return" />
        <SummaryStat icon={Layers} label="Total AUM" value={fmtUsd(summary?.total_aum)} testid="summary-aum" />
        <SummaryStat icon={Activity} label="Active / Total" value={`${summary?.active ?? 0} / ${summary?.accounts ?? 0}`} testid="summary-active" />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {accounts.map((a, i) => {
            const ex = getExchange(a.exchange_slug);
            const m = a.metrics || {};
            return (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="rounded-md border border-[#262626] bg-[#141414] hover:border-[#333333] transition-colors flex flex-col"
                data-testid={`portfolio-card-${a.id}`}
              >
                <div className="flex items-center gap-3 px-4 py-4 border-b border-[#262626]">
                  <div className="relative">
                    <span className="w-10 h-10 rounded-md bg-[#0A0A0A] border border-[#262626] flex items-center justify-center font-heading font-bold text-xs" style={{ color: ex.color }}>{ex.name.slice(0, 2).toUpperCase()}</span>
                    <span className={cn("absolute -bottom-1 -right-1 w-4.5 h-4.5 w-4 h-4 rounded-full border-2 border-[#141414] flex items-center justify-center", a.account_role === "master" ? "bg-[#D4AF37]" : "bg-[#333333]")}>
                      {a.account_role === "master" ? <Crown className="w-2 h-2 text-[#0A0A0A]" /> : <Copy className="w-2 h-2 text-[#F5F5F5]" />}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#F5F5F5] truncate">{a.account_name}</div>
                    <div className="text-[11px] text-[#737373]">{ex.name}</div>
                  </div>
                  <RoleBadge role={a.account_role} />
                </div>

                <div className="px-4 pt-3 pb-2 h-[52px]">
                  <Sparkline series={m.pnl_series} height={44} />
                </div>

                <div className="grid grid-cols-2 gap-y-3 gap-x-4 px-4 py-3 border-t border-[#1c1c1c]">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#737373]">Total Return</div>
                    <div className={cn("font-mono text-base font-semibold mt-0.5", pnlColor(m.total_return))}>{m.total_return != null ? fmtPct(m.total_return) : "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#737373]">Balance</div>
                    <div className="font-mono text-base font-semibold text-[#F5F5F5] mt-0.5">{a.balance != null ? fmtUsd(a.balance) : "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#737373]">Active Days</div>
                    <div className="font-mono text-sm text-[#A3A3A3] mt-0.5">{m.active_days ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#737373]">Max Drawdown</div>
                    <div className="font-mono text-sm text-[#EF4444] mt-0.5">{m.drawdown != null ? `-${m.drawdown}%` : "—"}</div>
                  </div>
                </div>

                <button onClick={() => navigate(`/portfolio/${a.id}`)} data-testid={`stats-reports-btn-${a.id}`}
                  className="mt-auto flex items-center justify-center gap-2 border-t border-[#262626] py-2.5 text-sm text-[#D4AF37] hover:bg-[rgba(212,175,55,0.06)] transition-colors">
                  <BarChart3 className="w-4 h-4" /> Stats and Reports
                </button>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
